import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import '@fortawesome/fontawesome-free/css/all.min.css';
import Navbar from '../components/Header/Navbar';
import Footer from '../components/Footer';
import SbPageHeader from '../components/layout/SbPageHeader';
import SbEmptyState from '../components/layout/SbEmptyState';
import SbInlineSearch from '../components/layout/SbInlineSearch';
import Button from '../components/Button';
import Modal from '../components/Modal/Modal';
import MyReelCard from '../components/reels/MyReelCard';
import ReelDeleteConfirmModal from '../components/reels/ReelDeleteConfirmModal';
import ReelPlayer from '../components/reels/ReelPlayer';
import { useAuth } from '../contexts/AuthContext';
import { useMyReels } from '../hooks/useMyReels';
import { useDeleteReel } from '../hooks/useDeleteReel';
import { useSavedReels } from '../hooks/useSavedReels';
import { useSaveReel } from '../hooks/useSaveReel';
import type { ReelMineViewModel } from '../api/types/reel';

type ActiveTab = 'my-reels' | 'saved-reels';

const Gonderilerim: React.FC = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();

  const [activeTab, setActiveTab] = useState<ActiveTab>('my-reels');
  const [playingReelUrl, setPlayingReelUrl] = useState<string | null>(null);

  const { reels, loading, error, refetch, removeReel } = useMyReels(isAuthenticated && Boolean(user?.id));
  const { deleteReel, loading: deleteLoading, error: deleteError, resetError: resetDeleteError } = useDeleteReel();

  const savedReels = useSavedReels(isAuthenticated && Boolean(user?.id));
  const { toggleSave, loading: saveLoading } = useSaveReel();

  const [reelPendingDelete, setReelPendingDelete] = useState<ReelMineViewModel | null>(null);
  const [infoModal, setInfoModal] = useState<{ title: string; message: string } | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/giris', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const requestDelete = useCallback((reel: ReelMineViewModel) => {
    resetDeleteError();
    setReelPendingDelete(reel);
  }, [resetDeleteError]);

  const cancelDelete = useCallback(() => {
    if (deleteLoading) return;
    setReelPendingDelete(null);
    resetDeleteError();
  }, [deleteLoading, resetDeleteError]);

  const confirmDelete = useCallback(async () => {
    if (!reelPendingDelete || deleteLoading) return;

    const reelId = reelPendingDelete.id;
    const result = await deleteReel(reelId);
    if (result.ok) {
      removeReel(reelId);
      setReelPendingDelete(null);
      setInfoModal({
        title: 'Gönderi silindi',
        message: result.message,
      });
      return;
    }

    setInfoModal({
      title: 'Gönderi silinemedi',
      message: result.message,
    });
  }, [reelPendingDelete, deleteLoading, deleteReel, removeReel]);

  const handleUnsaveSavedReel = useCallback(async (reelId: string) => {
    const ok = await toggleSave(reelId, true);
    if (ok) savedReels.removeReel(reelId);
  }, [toggleSave, savedReels]);

  if (!isAuthenticated) return null;

  if (!user) {
    return (
      <div className="min-vh-100" style={{ backgroundColor: 'var(--sb-bg-muted)' }}>
        <Navbar />
        <main className="container py-4">
          <div className="text-center py-5 text-muted">Yükleniyor...</div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-vh-100" style={{ backgroundColor: 'var(--sb-bg-muted)' }}>
      <Navbar />

      <main className="sb-page-container py-4 sb-page-shell">
        <SbPageHeader
          title="Gönderilerim"
          subtitle="Paylaştığınız içerikleri buradan takip edin."
          backTo={{ to: '/', label: 'Ana Sayfaya Dön' }}
          actions={
            <Link to="/gonderiler" className="btn btn-primary btn-sm">
              <i className="fas fa-plus me-1" aria-hidden />
              Gönderi Oluştur
            </Link>
          }
        />

        <div className="sb-tab-bar" role="tablist" aria-label="Gönderi sekmeleri">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'my-reels'}
            className={`sb-tab-bar__btn ${activeTab === 'my-reels' ? 'sb-tab-bar__btn--active' : ''}`}
            onClick={() => setActiveTab('my-reels')}
          >
            Tümü
            {reels.length > 0 && ` (${reels.length})`}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'saved-reels'}
            className={`sb-tab-bar__btn ${activeTab === 'saved-reels' ? 'sb-tab-bar__btn--active' : ''}`}
            onClick={() => setActiveTab('saved-reels')}
          >
            Kaydettiklerim
            {savedReels.reels.length > 0 && ` (${savedReels.reels.length})`}
          </button>
        </div>

        {(activeTab === 'my-reels' ? reels.length : savedReels.reels.length) > 0 && (
          <SbInlineSearch
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Gönderilerimde ara..."
            id="my-reels-search"
            className="mb-3"
          />
        )}

        {/* My Reels tab */}
        {activeTab === 'my-reels' && (
          <>
            {error && (
              <div className="alert alert-warning mb-3 d-flex flex-wrap align-items-center gap-2" role="alert">
                <span>{error}</span>
                <Button className="btn btn-sm btn-outline-primary" onClick={() => refetch()}>
                  Tekrar Dene
                </Button>
              </div>
            )}

            {loading ? (
              <div className="text-center py-5 text-muted">Yükleniyor...</div>
            ) : !error && reels.length === 0 ? (
              <SbEmptyState
                iconClass="far fa-file-alt"
                title="Henüz gönderiniz yok"
                description="İlk gönderinizi oluşturarak topluluğunuzla paylaşmaya başlayın."
                actionLabel="Gönderi Oluştur"
                actionTo="/gonderiler"
              />
            ) : (
              <div className="row g-4">
                {reels
                  .filter((reel) => {
                    const q = searchQuery.trim().toLowerCase();
                    if (!q) return true;
                    return reel.id.toLowerCase().includes(q);
                  })
                  .map((reel) => (
                  <div key={reel.id} className="col-12 col-sm-6 col-md-4 col-lg-3">
                    <MyReelCard
                      reel={reel}
                      onDelete={requestDelete}
                      onPlay={(r) => setPlayingReelUrl(r.videoUrl)}
                      deleteDisabled={deleteLoading}
                    />
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Saved Reels tab */}
        {activeTab === 'saved-reels' && (
          <>
            {savedReels.error && (
              <div className="alert alert-warning mb-3 d-flex flex-wrap align-items-center gap-2" role="alert">
                <span>{savedReels.error}</span>
                <Button className="btn btn-sm btn-outline-primary" onClick={() => void savedReels.refetch()}>
                  Tekrar Dene
                </Button>
              </div>
            )}

            {savedReels.loading ? (
              <div className="text-center py-5 text-muted">Yükleniyor...</div>
            ) : !savedReels.error && savedReels.reels.length === 0 ? (
              <SbEmptyState
                iconClass="far fa-bookmark"
                title="Henüz kaydedilmiş gönderi yok"
                description="Gönderiler akışından beğendiğiniz içerikleri kaydedebilirsiniz."
                actionLabel="Gönderilere Git"
                actionTo="/gonderiler"
              />
            ) : (
              <div className="row g-4">
                {savedReels.reels
                  .filter((reel) => {
                    const q = searchQuery.trim().toLowerCase();
                    if (!q) return true;
                    return reel.id.toLowerCase().includes(q);
                  })
                  .map((reel) => (
                  <div key={reel.id} className="col-12 col-sm-6 col-md-4 col-lg-3">
                    <div className="card border-0 shadow-sm h-100">
                      <button
                        type="button"
                        className="p-0 border-0 bg-transparent w-100 position-relative"
                        style={{ cursor: 'pointer' }}
                        onClick={() => setPlayingReelUrl(reel.videoUrl)}
                        aria-label="Reeli oynat"
                      >
                        <video
                          src={reel.videoUrl}
                          muted
                          preload="metadata"
                          playsInline
                          style={{ width: '100%', height: 220, objectFit: 'cover', display: 'block' }}
                        />
                        <div
                          className="position-absolute top-50 start-50 translate-middle rounded-circle d-flex align-items-center justify-content-center"
                          style={{ width: 48, height: 48, background: 'rgba(0,0,0,0.55)', pointerEvents: 'none' }}
                        >
                          <i className="fas fa-play text-white" style={{ marginLeft: 3 }} />
                        </div>
                      </button>
                      <div className="card-body d-flex justify-content-between align-items-center">
                        <small className="text-muted">Kaydedildi</small>
                        <Button
                          className="btn btn-outline-danger btn-sm"
                          onClick={() => void handleUnsaveSavedReel(reel.id)}
                          disabled={saveLoading}
                        >
                          <i className="fas fa-bookmark me-1" />
                          Kaldır
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </main>

      <Footer />

      {/* Reel video playback modal */}
      {playingReelUrl && (
        <div
          className="modal fade show d-block"
          tabIndex={-1}
          role="dialog"
          aria-modal="true"
          style={{ backgroundColor: 'rgba(0,0,0,0.8)' }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setPlayingReelUrl(null);
          }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content bg-transparent border-0">
              <div className="modal-header border-0 pb-0">
                <button
                  type="button"
                  className="btn-close btn-close-white ms-auto"
                  aria-label="Kapat"
                  onClick={() => setPlayingReelUrl(null)}
                />
              </div>
              <div className="modal-body p-0 d-flex justify-content-center">
                <div
                  style={{
                    width: 'min(90vw, 420px)',
                    height: '70vh',
                    borderRadius: '0.75rem',
                    overflow: 'hidden',
                    background: '#000',
                  }}
                >
                  <ReelPlayer videoUrl={playingReelUrl} isActive playWithAudio />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {infoModal && (
        <Modal
          title={infoModal.title}
          message={infoModal.message}
          onClose={() => setInfoModal(null)}
        />
      )}

      <ReelDeleteConfirmModal
        show={Boolean(reelPendingDelete)}
        loading={deleteLoading}
        error={deleteError}
        onConfirm={() => void confirmDelete()}
        onCancel={cancelDelete}
      />
    </div>
  );
};

export default Gonderilerim;
